// src/pages/LandingPage.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronLeft } from "lucide-react";

export default function LandingPage() {
  const [fadeIn, setFadeIn] = useState({ about: false, features: false, blog: false });
  const [blogs, setBlogs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleSidebar = () => {
    setMobileOpen(!mobileOpen);
  };

  // Scroll-triggered fade-in
  useEffect(() => {
    const handleScroll = () => {
      const aboutSection = document.getElementById("about");
      const featuresSection = document.getElementById("features");
      const blogSection = document.getElementById("blog-section");

      const windowHeight = window.innerHeight;

      if (aboutSection && aboutSection.getBoundingClientRect().top < windowHeight - 100) {
        setFadeIn((prev) => ({ ...prev, about: true }));
      }
      if (featuresSection && featuresSection.getBoundingClientRect().top < windowHeight - 100) {
        setFadeIn((prev) => ({ ...prev, features: true }));
      }
      if (blogSection && blogSection.getBoundingClientRect().top < windowHeight - 100) {
        setFadeIn((prev) => ({ ...prev, blog: true }));
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetch("https://interviewnotebook.com/api/blog")
      .then((res) => res.json())
      .then((data) => {
        const blogsArray = Array.isArray(data) ? data : data.blogs || [];
        const mappedBlogs = blogsArray.map((blog) => ({
          ...blog,
          imageUrl: blog.coverImage || "/placeholder.png",
        }));
        setBlogs(mappedBlogs.slice(0, 2));
      })
      .catch((err) => console.error("Error fetching blogs:", err));
  }, []);

  const aboutCards = [
    { icon: "💼", title: "Track Company Interview Experience", desc: "Record your previous company interviews with all rounds and questions for reference and improvement." },
    { icon: "📘", title: "Manage Questions", desc: "Add, edit, and organize questions for practice across different rounds and topics." },
    { icon: "🎤", title: "Mock Interviews", desc: "Practice with self-interviews or mock sessions to boost confidence." },
    { icon: "📝", title: "Notes & Study Materials", desc: "Upload PDFs, docs, and create your personal notes for quick revision." },
    { icon: "📊", title: "Dashboard & Tracking", desc: "Receive notifications, filter interviews by status or company, and track your preparation journey." },
  ];

  const featureCards = [
    { icon: "🤖", title: "AI Copilot", desc: "Limited use for now: generate and organize interview questions intelligently." },
    { icon: "📈", title: "Analytics Dashboard", desc: "Track interviews, rounds completed, and candidate progress. Insights for better preparation." },
    { icon: "🏢", title: "HR Platform", desc: "Separate platform for HRs to manage interviews, rounds, and candidate evaluation efficiently." },
  ];

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false); // Close sidebar on mobile
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col scroll-smooth">
      {/* ====== Navbar ====== */}
      <nav className="fixed w-full bg-white shadow z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-indigo-600">InterviewNotebook</h1>

          {/* Desktop nav links */}
          <div className="space-x-6 hidden md:flex">
            <button onClick={() => scrollToSection("home")} className="text-gray-700 font-semibold hover:text-indigo-600 transition">Home</button>
            <button onClick={() => scrollToSection("about")} className="text-gray-700 font-semibold hover:text-indigo-600 transition">About</button>
            <button onClick={() => scrollToSection("features")} className="text-gray-700 font-semibold hover:text-indigo-600 transition">Features</button>
            <button onClick={() => scrollToSection("blog-section")} className="text-gray-700 font-semibold hover:text-indigo-600 transition">Blog</button>
            <button onClick={() => scrollToSection("how-it-works")} className="text-gray-700 font-semibold hover:text-indigo-600 transition">How It Works</button>
            <a href="/contact-us" className="text-gray-700 font-semibold hover:text-indigo-600 transition">Contact Us</a>
            <a href="/login" className="text-gray-700 font-semibold hover:text-indigo-600 transition">Login/Register</a>
            <a href="/admin/login" className="text-gray-700 font-semibold hover:text-indigo-600 transition">Admin Login</a>
          </div>

          {/* Mobile hamburger */}
          <button onClick={toggleSidebar} className="md:hidden text-gray-800">
            <Menu size={24} />
          </button>
        </div>
      </nav>
{/* ====== Mobile Sidebar ====== */}
<aside
  className={`fixed z-50 top-0 left-0 h-full 
    bg-gradient-to-b from-indigo-600 via-purple-600 to-pink-500 
    text-white shadow-lg transition-transform duration-300 md:hidden 
    w-full max-w-xs ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
>
  <div className="flex items-center justify-between p-4 border-b border-white/30">
    <h2 className="font-bold text-lg">Menu</h2>
    <button onClick={toggleSidebar} className="text-white">
      <ChevronLeft size={24} />
    </button>
  </div>

  <nav className="flex flex-col p-4 space-y-4">
    {/* First line links */}
    <div className="flex flex-wrap justify-center gap-4 border-b border-white/30 pb-4">
      <button
        onClick={() => {
          scrollToSection("home");
          setMobileOpen(false);
        }}
        className="font-medium hover:text-gray-200"
      >
        Home
      </button>
      <button
        onClick={() => {
          scrollToSection("about");
          setMobileOpen(false);
        }}
        className="font-medium hover:text-gray-200"
      >
        About
      </button>
      <button
        onClick={() => {
          scrollToSection("features");
          setMobileOpen(false);
        }}
        className="font-medium hover:text-gray-200"
      >
        Features
      </button>
      <button
        onClick={() => {
          scrollToSection("how-it-works");
          setMobileOpen(false);
        }}
        className="font-medium hover:text-gray-200"
      >
        How It Works
      </button>
    </div>

    {/* Second line links */}
    <div className="flex flex-wrap justify-center gap-4">
      <Link
        to="/contact-us"
        className="font-medium hover:text-gray-200"
        onClick={() => setMobileOpen(false)}
      >
        Contact Us
      </Link>
      <Link
        to="/login"
        className="font-medium hover:text-gray-200"
        onClick={() => setMobileOpen(false)}
      >
        Login/Register
      </Link>
      <Link
        to="/admin/login"
        className="font-medium hover:text-gray-200"
        onClick={() => setMobileOpen(false)}
      >
        Admin Login
      </Link>
    </div>
  </nav>
</aside>


      {/* ====== Mobile Overlay ====== */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black opacity-50 z-40 md:hidden" />
      )}

      {/* ====== Hero ====== */}
      <header id="home" className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white mt-16">
        <div className="max-w-6xl mx-auto px-6 py-28 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fadeIn">
            NotePrep & Track – Smart Interview Preparation
          </h1>
          <p className="text-lg md:text-xl mb-6 animate-fadeIn delay-150">
            Your personal diary app for interview prep – manage questions, mock interviews,
            self-prep notes, and track your progress efficiently.
          </p>
          <div className="flex justify-center gap-4">
            <a href="/login" className="bg-white text-indigo-600 font-semibold px-6 py-3 rounded-lg shadow hover:bg-gray-100 transition animate-fadeIn delay-300">
              Get Started
            </a>
            <button onClick={() => scrollToSection("how-it-works")} className="bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-indigo-700 transition animate-fadeIn delay-400">
              How It Works
            </button>
          </div>
        </div>
      </header>

      {/* ====== About ====== */}
      <section
        id="about"
        className={`py-20 bg-white max-w-6xl mx-auto px-6 text-center transition-opacity duration-1000 ${fadeIn.about ? "opacity-100" : "opacity-0"}`}
      >
        <h2 className="text-3xl font-bold mb-6">Why We Built NotePrep & Track</h2>
        <p className="text-gray-600 text-lg mb-12">
          NotePrep & Track helps candidates stay organized, confident, and ready.
        </p>
        <div className="grid md:grid-cols-2 gap-8 text-left">
          {aboutCards.map((card, idx) => (
            <div key={idx} className="p-6 bg-gray-50 rounded-xl shadow-sm hover:shadow-lg hover:scale-105 transition-transform duration-500" style={{ transitionDelay: `${idx * 150}ms` }}>
              <h3 className="text-xl font-semibold mb-3">{card.icon} {card.title}</h3>
              <p className="text-gray-600">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== Features ====== */}
      <section
        id="features"
        className={`py-20 bg-gray-50 max-w-6xl mx-auto px-6 text-center transition-opacity duration-1000 ${fadeIn.features ? "opacity-100" : "opacity-0"}`}
      >
        <h2 className="text-3xl font-bold mb-6">Upcoming & AI Features</h2>
        <p className="text-gray-600 text-lg mb-12">
          We're also building a separate HR platform for companies. The AI Copilot feature assists in generating interview questions (limited use for now). Analytics dashboard tracks interviews and rounds completed.
        </p>
        <div className="grid md:grid-cols-3 gap-8 text-left">
          {featureCards.map((card, idx) => (
            <div key={idx} className="p-6 bg-white rounded-xl shadow-sm hover:shadow-lg hover:scale-105 transition-transform duration-500" style={{ transitionDelay: `${idx * 200}ms` }}>
              <h3 className="text-xl font-semibold mb-3">{card.icon} {card.title}</h3>
              <p className="text-gray-600">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== Blog Section ====== */}
      <section
        id="blog-section"
        className={`py-20 bg-white max-w-6xl mx-auto px-6 text-center transition-opacity duration-1000 ${fadeIn.blog ? "opacity-100" : "opacity-0"}`}
      >
        <h2 className="text-3xl font-bold mb-6">Latest from Our Blog</h2>
        <p className="text-gray-600 text-lg mb-12">
          Tips, insights, and stories to help you ace your interview prep.
        </p>
        <div className="grid md:grid-cols-2 gap-8">
          {blogs.length > 0 ? (
            blogs.map((blog) => (
              <div key={blog.id} className="p-6 bg-gray-50 rounded-xl shadow hover:shadow-lg hover:scale-105 transition-transform duration-500 text-left">
                <img src={blog.imageUrl} alt={blog.title} className="rounded-lg mb-4 w-full h-48 object-cover" />
                <h3 className="text-xl font-semibold mb-2">{blog.title}</h3>
                <div className="text-gray-600 mb-3 line-clamp-3" dangerouslySetInnerHTML={{ __html: blog.content }} />
                <Link to={`/blogs/${blog.slug}`} className="text-indigo-600 font-semibold hover:underline">Read More →</Link>
              </div>
            ))
          ) : (
            <p className="text-gray-500">No blogs yet. Stay tuned!</p>
          )}
        </div>
        <div className="mt-10">
          <Link to="/blogs" className="bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-indigo-700 transition">View All Blogs</Link>
        </div>
      </section>

      {/* ====== How It Works ====== */}
      <section id="how-it-works" className="py-20 bg-gray-50 max-w-6xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold mb-12">How InterviewNotebook Works</h2>
        <p className="text-gray-600 text-lg mb-12">
          Check out our interactive How It Works page to see step-by-step instructions and screenshots.
        </p>
        <a href="/how-it-works" className="bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-indigo-700 transition">
          Open How It Works Page
        </a>
      </section>

      {/* ====== Footer ====== */}
      <footer className="bg-gray-900 text-gray-300 py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between">
          <p className="text-sm">© {new Date().getFullYear()} NotePrep & Track. All rights reserved.</p>
          <p className="text-sm mt-4 md:mt-0">
            Built with ❤️ by <span className="font-semibold text-white">Samir Kumar (Founder)</span> &{" "}
            <span className="font-semibold text-white">[Rajesh Kumar] (Co-Founder)</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
